import { useRef } from 'react';

import { useAppSelector } from 'store/store-hooks';
import { findFcPosition } from 'components/app/_helpers/_app.helper';
import { selectHelpMode } from 'components/app/_mediator/app-data.slice';
import { useMemory } from 'components/app/_mediator/memory/use-memory';
import { useMemoryFc } from 'components/app/_mediator/memory/use-memory-fc';
import { useZoomFc } from 'components/app/_mediator/zoom/use-zoom-effects';
import { useZoomCb } from 'components/app/_mediator/zoom/use-zoom-cb';
import { useClipThumbPrevContext } from 'components/app/_mediator/clip-thumb-prev.provider';
import { useFinalcut } from 'components/app/finalcuts/_mediator/use-finalcut';
import { useFinalcutCb } from 'components/app/finalcuts/_mediator/use-finalcut-cb';

import { Tooltip } from 'components/UI/tooltip/tooltip';
import { ClipThumbPrev } from 'components/app/app-components/clip-thumb-prev/clip-thumb-prev';
import { ClipErrorStatusBar } from 'components/app/app-components/clip-error-status-bar/clip-error-status-bar';
import { Track, TrackHeader, TrackBody, TrackFeed } from 'components/app/app-components/track-container/track-elements';
import { FinalcutsHeaderInfo } from 'components/app/finalcuts/_elements/finalcuts-header-info/finalcuts-header-info';
import { FinalcutsCell } from './finalcuts-cell';

export const FinalcutTrack = () => {
  const { keepCursorInView } = useZoomCb();
  const { resetHoveredClip } = useClipThumbPrevContext();
  const { fcStoryBoard, fcCells, fcErrorClips } = useAppSelector((state) => state.fc__data);
  const snapshot = useAppSelector((state) => state.memory.finalcut);
  const helpMode = useAppSelector(selectHelpMode);

  const container = useRef<HTMLDivElement>(null);
  const feed = useRef<HTMLDivElement>(null);

  useMemory(snapshot, useMemoryFc());
  useZoomFc(container, feed);
  useFinalcut();

  const { fcSelectClip } = useFinalcutCb();

  const handleErrorClick = (clipId: ClipId) => {
    const position = findFcPosition(clipId, fcStoryBoard) ?? 0;
    fcSelectClip(position);
    keepCursorInView();
  };

  return (
    <Track>
      <Tooltip
        tip="Final Cut track: A snapshot of your storyboard — use this track to navigate back to clips on your storyboard (if needed)"
        showTooltip={helpMode}
        position="bottom"
      >
        <TrackHeader>
          <FinalcutsHeaderInfo />
        </TrackHeader>
      </Tooltip>
      <TrackBody ref={container}>
        <TrackFeed ref={feed} onMouseLeave={resetHoveredClip}>
          {fcStoryBoard.map((el, i) => (
            <FinalcutsCell key={i} index={i} clipId={el} width={fcCells?.width[i] ?? 0} />
          ))}
        </TrackFeed>
      </TrackBody>
      <ClipThumbPrev />
      <ClipErrorStatusBar errorClips={Object.keys(fcErrorClips)} handleClick={handleErrorClick} />
    </Track>
  );
};
